import { createInstancedRenderer } from "./instancedRenderer";
import { paintLines } from "../canvas2d/paintLines";

// Instance: [x0, y0, x1, y1] in data units, [r, g, b, a], [width]
const LINE_ATTRIBUTE_COMPONENTS = [4, 4, 1];

// Each segment is a quad stretched along the segment and widened along its
// normal. uWidthScale turns the instance width into device px; uCapRatio
// extends both ends by that share of the width, so the segments of a
// polyline overlap at the joints without gaps.
const LINE_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 aCorner;
layout(location = 1) in vec4 aSegment;
layout(location = 2) in vec4 aColor;
layout(location = 3) in float aWidth;

uniform vec2 uViewport;
uniform vec2 uX;
uniform vec2 uY;
uniform float uWidthScale;
uniform float uCapRatio;

out vec4 vColor;

void main() {
  vec2 start = vec2(aSegment.x * uX.x + uX.y, aSegment.y * uY.x + uY.y);
  vec2 end = vec2(aSegment.z * uX.x + uX.y, aSegment.w * uY.x + uY.y);
  vec2 delta = end - start;
  float segmentLength = length(delta);
  vec2 direction = segmentLength > 0.0 ? delta / segmentLength : vec2(1.0, 0.0);
  vec2 normal = vec2(-direction.y, direction.x);
  float width = aWidth * uWidthScale;
  vec2 cap = direction * width * uCapRatio;
  vec2 position = mix(start - cap, end + cap, aCorner.x) + normal * (aCorner.y - 0.5) * width;
  vec2 clip = position / uViewport * 2.0 - 1.0;

  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  vColor = aColor;
}
`;

/**
 * Instanced thick line segments: the grid, the last price line, the Line
 * chart and indicator lines.
 */
export const createLineRenderer = () => {
  const renderer = createInstancedRenderer({
    vertexShader: LINE_VERTEX_SHADER,
    uniformNames: ["uViewport", "uX", "uY", "uWidthScale", "uCapRatio"],
    attributeComponents: LINE_ATTRIBUTE_COMPONENTS,
  });

  return {
    floatsPerInstance: renderer.floatsPerInstance,
    init: renderer.init,
    release: renderer.release,
    setInstances: renderer.setInstances,
    updateInstances: renderer.updateInstances,

    /**
     * @param {object} params
     * @param {number[]} params.viewport   - [width, height] of the canvas, device px
     * @param {number[]} params.xTransform - [scale, offset]: data x → device px
     * @param {number[]} params.yTransform - [scale, offset]: data y → device px
     * @param {number} [params.widthScale] - instance width → device px; 1 when widths are device px
     * @param {number} [params.capRatio] - ends extended by this share of the width; 0 for exact ends
     */
    draw: (params) => {
      const { viewport, xTransform, yTransform, widthScale = 1, capRatio = 0 } = params;

      renderer.draw(
        (gl, uniforms) => {
          gl.uniform2fv(uniforms.uViewport, viewport);
          gl.uniform2fv(uniforms.uX, xTransform);
          gl.uniform2fv(uniforms.uY, yTransform);
          gl.uniform1f(uniforms.uWidthScale, widthScale);
          gl.uniform1f(uniforms.uCapRatio, capRatio);
        },
        (context, instances) => paintLines(context, instances, renderer.floatsPerInstance, params),
      );
    },
  };
};
